import { useRef, useState } from "react";
import { Field, inputClass } from "../routes/SignIn.js";
import { ACCEPTED_CERT_TYPES } from "../portal/api.js";
import { Card, cn, PrimaryButton, SecondaryButton, ShieldCheckIcon } from "./ui.js";

/**
 * Step 5 — verification. Deliberately minimal: just enough to establish trust,
 * not full compliance onboarding (the goal is activation). License number,
 * insurance details, and an optional insurance-certificate upload (straight to
 * a private bucket via a presigned PUT).
 */
export interface VerifyInput {
  licenseNumber: string;
  insuranceInfo: string;
  insuranceCertKey?: string;
  insuranceCertName?: string;
}

export function VerifyStep({
  initial,
  onUpload,
  onContinue,
  onSkip,
}: {
  initial: VerifyInput;
  onUpload: (file: File) => Promise<{ key: string; name: string }>;
  onContinue: (values: VerifyInput) => void;
  onSkip: () => void;
}) {
  const [licenseNumber, setLicenseNumber] = useState(initial.licenseNumber);
  const [insuranceInfo, setInsuranceInfo] = useState(initial.insuranceInfo);
  const [cert, setCert] = useState<{ key?: string; name?: string }>({
    ...(initial.insuranceCertKey ? { key: initial.insuranceCertKey } : {}),
    ...(initial.insuranceCertName ? { name: initial.insuranceCertName } : {}),
  });
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setUploadError(null);
    setUploading(true);
    try {
      const { key, name } = await onUpload(file);
      setCert({ key, name });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  function submit() {
    onContinue({
      licenseNumber: licenseNumber.trim(),
      insuranceInfo: insuranceInfo.trim(),
      ...(cert.key ? { insuranceCertKey: cert.key } : {}),
      ...(cert.name ? { insuranceCertName: cert.name } : {}),
    });
  }

  return (
    <main className="mx-auto max-w-md px-6 py-8">
      <h1 className="text-2xl font-bold text-charcoal">Verify your business</h1>
      <p className="mt-2 text-sm text-charcoal">
        A couple of trust details help us approve you faster. You can add more later — this is all
        optional to start.
      </p>

      <div className="mt-7 space-y-5">
        <Card icon={<ShieldCheckIcon />} title="Licensing &amp; insurance">
          <Field label="Business license number">
            <input
              className={inputClass}
              value={licenseNumber}
              onChange={(e) => setLicenseNumber(e.target.value)}
              placeholder="If applicable"
            />
          </Field>
          <Field label="Insurance">
            <textarea
              className={cn(inputClass, "min-h-[72px] py-2")}
              rows={2}
              value={insuranceInfo}
              onChange={(e) => setInsuranceInfo(e.target.value)}
              placeholder="Carrier and policy details, if any"
            />
          </Field>
          <div>
            <span className="mb-1 block text-sm font-medium text-charcoal">
              Insurance certificate (optional)
            </span>
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPTED_CERT_TYPES}
              onChange={onFile}
              className="hidden"
            />
            {cert.name ? (
              <div className="flex items-center justify-between gap-2 rounded-xl border border-brand-200 bg-brand-50 px-3 py-2">
                <span className="truncate text-sm text-brand-800">✓ {cert.name}</span>
                <button
                  type="button"
                  onClick={() => setCert({})}
                  className="shrink-0 text-sm font-medium text-charcoal"
                >
                  Remove
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-charcoal/30 px-4 py-3 text-sm font-medium text-brand-700 active:bg-field-gray disabled:opacity-60"
              >
                {uploading ? "Uploading…" : "Upload PDF or photo"}
              </button>
            )}
            {uploadError && <p className="mt-1 text-sm text-red-600">{uploadError}</p>}
            <p className="mt-1 text-xs text-charcoal">PDF, JPG, PNG or WEBP — up to 10 MB.</p>
          </div>
        </Card>
      </div>

      <div className="mt-8 space-y-3">
        <PrimaryButton onClick={submit} disabled={uploading}>
          Continue
        </PrimaryButton>
        <SecondaryButton onClick={onSkip}>Skip for now</SecondaryButton>
      </div>
    </main>
  );
}
